import React, { useMemo, useState, useRef, useEffect } from "react";
import { Heart } from "lucide-react";
import "./businessCalendar.css";
import { supabase } from "../../supabase_client.jsx";


// Need date to be uniform ie: "YYYY-MM-DD"
function formatDateKey(date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}


function formatDisplayDate(isoDate) {
  // Split manually to avoid UTC->local timezone shift
  const [year, month, day] = String(isoDate).split("-").map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}


export default function BusinessCalendar({ currentUser, onViewSelect, onProfileSetupComplete }) {
  const handleProfileSetupComplete = () => {
    setIsProfileComplete(true);
    if (onProfileSetupComplete) onProfileSetupComplete();
  };
  // The calnder step up ie: dates, event data etc
  const businessUserId = currentUser?.user?.id || currentUser?.id;

  const [events, setEvents] = useState([]);
  const [showAllEvents, setShowAllEvents] = useState(false); // For month view "more" toggle
  const [businessUsername, setBusinessUsername] = useState(null);

  const today = new Date();
  const [currentDate, setCurrentDate] = useState(today); 
  const [view, setView] = useState("month"); //default veiw
  const [isViewMenuOpen, setIsViewMenuOpen] = useState(false);

  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const weekdayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

  const [editEvent, setEditEvent] = useState(null);

  const isOwnEvent = (event) =>
    businessUsername &&
    (event.event_owner || "").toLowerCase() === businessUsername.toLowerCase();

  const [logoUrl, setLogoUrl] = useState("");
  const [isProfileComplete, setIsProfileComplete] = useState(true);

  const initials = useMemo(() => {
  if (!businessUsername) return "B";
  return businessUsername.trim().split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  }, [businessUsername]);
  // Step 1: resolve username once on mount
  useEffect(() => {
    if (!businessUserId) return;

    const resolveUsername = async () => {
      const { data: userData, error: userError } = await supabase
        .from("users")
        .select("username")
        .eq("user_id", businessUserId)
        .single();

      if (userError || !userData?.username) {
        console.log("DEBUG: User resolution failed", userError);
        return;
      }

      setBusinessUsername(userData.username.trim());

      const { data: bizData } = await supabase
        .from("businesses")
        .select("logo_url, has_setup_profile")
        .eq("user_id", businessUserId)
        .single();

      setLogoUrl(bizData?.logo_url || "");
      setIsProfileComplete(!bizData || bizData.has_setup_profile === true); 
    };

    resolveUsername();
  }, [businessUserId]);

  // Step 2: fetch events whenever username or toggle changes
  useEffect(() => {
    const fetchEvents = async () => {
      let query = supabase.from("events").select("*").eq("is_approved", true);

      if (!showAllEvents && businessUsername) {
        query = query.ilike("event_owner", businessUsername);
      }
      // If showAllEvents, no owner filter — fetch everything

      const { data, error } = await query;

      if (error) {
        console.error("DEBUG: Supabase fetch error:", error.message);
        setEvents([]);
        return;
      }

      setEvents(data || []);
    };

    // Only fetch once we have the username (or if showing all events)
    if (showAllEvents || businessUsername) {
      fetchEvents();
    }
  }, [businessUsername, showAllEvents]);

  const eventsByDateKey = {};
  events.forEach((evt) => {
    const key = evt.date; 
    if (!eventsByDateKey[key]) eventsByDateKey[key] = [];
    eventsByDateKey[key].push(evt);
  });

  // hover pop up
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [hoverPosition, setHoverPosition] = useState(null);
  const containerRef = useRef(null);

  const handleEventMouseEnter = (eventData, e) => {
    const containerRect = containerRef.current?.getBoundingClientRect();
    const targetRect = e.currentTarget.getBoundingClientRect();
    if (!containerRect) return;

    const top = targetRect.bottom - containerRect.top + 4;
    const left = targetRect.left - containerRect.left;

    setHoveredEvent(eventData);
    setHoverPosition({ top, left });
  };

  const handleEventMouseLeave = () => {
    setHoveredEvent(null);
    setHoverPosition(null);
  };

  // Event side panell pop up thingy 
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [bookmarkedIds, setBookmarkedIds] = useState([]); 

  const handleEventClick = (eventData) => {
    setSelectedEvent(eventData);
  };

  const closeDetailPanel = () => {
    setSelectedEvent(null);
  };

  const toggleBookmark = (eventId) => {
    setBookmarkedIds((prev) =>
      prev.includes(eventId)
        ? prev.filter((id) => id !== eventId)
        : [...prev, eventId]
    );
  };

  const isSelectedBookmarked =
    selectedEvent && bookmarkedIds.includes(selectedEvent.id);

  // prev and next month controls
  const goToPrev = () => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      if (view === "month") {
        d.setMonth(d.getMonth() - 1);
      } else if (view === "week") {
        d.setDate(d.getDate() - 7);
      } else {
        d.setDate(d.getDate() - 1);
      }
      return d;
    });
  };

  const goToNext = () => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      if (view === "month") {
        d.setMonth(d.getMonth() + 1);
      } else if (view === "week") {
        d.setDate(d.getDate() + 7);
      } else {
        d.setDate(d.getDate() + 1);
      }
      return d;
    });
  };

  // Dot color: purple for own events, gray for others
  const getDotClass = (event) => {
    if (!showAllEvents) return "dot";
    const isOwn = businessUsername &&
      (event.event_owner || "").toLowerCase() === businessUsername.toLowerCase();
    return isOwn ? "dot" : "dot dot-other";
  };

  const renderMonthView = () => {
      const cells = [];

      // Add blank cells so day 1 lands on the correct weekday column
      const firstDayOfWeek = new Date(currentYear, currentMonth, 1).getDay();
      for (let i = 0; i < firstDayOfWeek; i++) {
        cells.push(<div key={`blank-${i}`} className="calendar-day calendar-day--empty" />);
      }

      for (let day = 1; day <= daysInMonth; day++) {
        const dateObj = new Date(currentYear, currentMonth, day);
        const key = formatDateKey(dateObj);
        const todaysEvents = eventsByDateKey[key] || [];

        cells.push(
          <div key={day} className="calendar-day">
            <div className="day-number">{day}</div>
            <div>
              {todaysEvents.slice(0, 3).map((event, idx) => (
                <div
                  key={idx}
                  className="event-dot"
                  onMouseEnter={(e) => handleEventMouseEnter(event, e)}
                  onMouseLeave={handleEventMouseLeave}
                  onClick={() => handleEventClick(event)}
                >
                  <span className={getDotClass(event)}></span>
                  <span className="event-title">{event.name}</span>
                </div>
              ))}
              {todaysEvents.length > 3 && (
                <div className="more-text">+ {todaysEvents.length - 3} more</div>
              )}
            </div>
          </div>
        );
      }
      return cells;
    };

    // WEEK VIEW
    const renderWeekView = () => {
      const cells = [];
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());

      for (let i = 0; i < 7; i++) {
        const dayDate = new Date(startOfWeek);
        dayDate.setDate(startOfWeek.getDate() + i);
        const key = formatDateKey(dayDate);
        const todaysEvents = eventsByDateKey[key] || [];

        cells.push(
          <div key={key} className="calendar-day">
            <div className="day-number">{dayDate.getMonth() + 1}/{dayDate.getDate()}</div>
            <div>
              {todaysEvents.slice(0, 4).map((event, idx) => (
                <div
                  key={idx}
                  className="event-dot"
                  onMouseEnter={(e) => handleEventMouseEnter(event, e)}
                  onMouseLeave={handleEventMouseLeave}
                  onClick={() => handleEventClick(event)}
                >
                  <span className={getDotClass(event)}></span>
                  <span className="event-title">{event.name}</span>
                </div>
              ))}
              {todaysEvents.length > 4 && (
                <div className="more-text">+ {todaysEvents.length - 4} more</div>
              )}
            </div>
          </div>
        );
      }
      return cells;
    };

    // DAY VIEW
    const renderDayView = () => {
      const key = formatDateKey(currentDate);
      const todaysEvents = eventsByDateKey[key] || [];

      return (
        <div className="calendar-day">
          <div className="day-view-label">
            {currentDate.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
          </div>
          <div className="day-view-events">
            {todaysEvents.length === 0 && (
              <div className="no-events-text">No events today.</div>
            )}
            {todaysEvents.map((event, idx) => (
              <div
                key={idx}
                className="day-view-event-card"
                onClick={() => handleEventClick(event)}
              >
                <div className="day-view-event-title">{event.name}</div>
                {event.address && <div className="day-view-event-line">{event.address}</div>}
                {(event.startTime || event.endTime) && (
                  <div className="day-view-event-line">
                    {event.startTime} {event.endTime && `- ${event.endTime}`}
                  </div>
                )}
                {event.description && (
                  <div className="day-view-event-desc">{event.description}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    };

    let gridContent;
    if (view === "month") gridContent = renderMonthView();
    else if (view === "week") gridContent = renderWeekView();
    else gridContent = renderDayView();

    const viewLabel = view.charAt(0).toUpperCase() + view.slice(1);

    const isPastEvent = useMemo(() => {
      if (!selectedEvent) return false;
  
      const now = new Date();
      
      // 1. Parse the Event Date (YYYY-MM-DD)
      const [year, month, day] = selectedEvent.date.split('-').map(Number);
      const eventDate = new Date(year, month - 1, day);
      
      // 2. If the date is strictly before today, it's past
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (eventDate < today) return true;
  
      // 3. If it's strictly after today, it's NOT past
      if (eventDate > today) return false;
  
      // 4. If it's TODAY, check the end time
      if (selectedEvent.endTime) {
        const [hours, minutes] = selectedEvent.endTime.split(':').map(Number);
        // Create a date object for Today at the Event's End Time
        const eventEndTime = new Date(year, month - 1, day, hours, minutes);
        
        return now > eventEndTime;
      }
  
      // If it's today but no end time is provided, assume it's available all day
      return false;
    }, [selectedEvent]);

    return (
      <div className="home-container">
      <div className="home-card">
        <div className="app-title-banner">
            <h1 className="app-title-text">Do You Wanna Go?</h1>
        </div>
      <div className="calendar-container" ref={containerRef}>

        {/* Top nav */}
        <div className="calendar-top-nav">
          <div className="nav-left">
            <button className="nav-tab" onClick={() => onViewSelect('businessHome')}>General</button>
            <button className="nav-tab active">Calendar</button>
            <button className="nav-tab" onClick={() => onViewSelect('businessCRMDash')}>Dashboard</button>
            <button className="nav-tab" onClick={() => onViewSelect('event-management')}>Event Management</button>
          </div>
          <div className="nav-right">
            <div className="nav-avatar-circle" style={{ position: "relative" }} onClick={() => onViewSelect("business-profile")}>
              {logoUrl
                ? <img src={logoUrl} alt="profile" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} />
                : initials
              }
              {!isProfileComplete && <span className="avatar-dot" />}
            </div>
          </div>
        </div>

        {/* Month/Week/Day row + toggle */}
        <div className="calendar-month-row">
          <div className="month-nav">
            <button className="chevron-button" onClick={goToPrev}>&lt;</button>
            <span className="month-label">{monthNames[currentMonth]} {currentYear}</span>
            <button className="chevron-button" onClick={goToNext}>&gt;</button>
          </div>

          <div className="month-actions">

            {/* My Events / All Events toggle */}
            <div className="events-toggle">
              <button
                className={"toggle-pill" + (!showAllEvents ? " toggle-pill-active" : "")}
                onClick={() => setShowAllEvents(false)}
              >
                My Events
              </button>
              <button
                className={"toggle-pill" + (showAllEvents ? " toggle-pill-active" : "")}
                onClick={() => setShowAllEvents(true)}
              >
                All Events
              </button>
            </div>
            
            {/* Add Event button */}
            <button
              className="view-button"
              onClick={() => onViewSelect('add-event')}
            >
              + Add Event
            </button>

            {/* View dropdown */}
            <div className="view-dropdown">
              <button className="view-button" onClick={() => setIsViewMenuOpen((prev) => !prev)}>
                {viewLabel}
                <span className="view-caret">▾</span>
              </button>
              {isViewMenuOpen && (
                <div className="view-menu">
                  {["month", "week", "day"].map((option) => (
                    <div
                      key={option}
                      className={"view-menu-item" + (view === option ? " active" : "")}
                      onClick={() => { setView(option); setIsViewMenuOpen(false); }}
                    >
                      {option.charAt(0).toUpperCase() + option.slice(1)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Legend when showing all events */}
        {showAllEvents && (
          <div className="events-legend">
            <span className="legend-item"><span className="dot"></span> Your events</span>
            <span className="legend-item"><span className="dot dot-other"></span> Other businesses</span>
          </div>
        )}

        {/* Weekday header */}
        {(view === "month" || view === "week") && (
          <div className="calendar-weekdays">
            {weekdayNames.map((day) => (
              <div key={day} className="weekday-cell">{day}</div>
            ))}
          </div>
        )}

        {/* Calendar grid */}
        <div className={`calendar-grid ${view}-view`}>{gridContent}</div>

        {/* Hover popup */}
        {hoveredEvent && hoverPosition && !selectedEvent && (
          <div className="event-hover-card" style={{ top: hoverPosition.top, left: hoverPosition.left }}>
            {hoveredEvent.image_url
              ? <img src={hoveredEvent.image_url} alt={hoveredEvent.name} className="hover-event-image" />
              : <div className="hover-image-placeholder">No Image</div>
            }
            <div className="hover-event-body">
              <div className="hover-event-title">{hoveredEvent.name}</div>
              <div className="hover-event-line">
                <span className="hover-label">Date: </span>
                {formatDisplayDate(hoveredEvent.date)}
              </div>
              {hoveredEvent.event_owner && (
                <div className="hover-event-line">
                  <span className="hover-label">By: </span>
                  {hoveredEvent.event_owner}
                </div>
              )}
              {hoveredEvent.address && (
                <div className="hover-event-line">
                  <span className="hover-label">Location: </span>
                  {hoveredEvent.address}
                </div>
              )}
              {hoveredEvent.description && (
                <div className="hover-event-desc">{hoveredEvent.description}</div>
              )}
            </div>
          </div>
        )}

        {/* Side panel */}
        {selectedEvent && (
          <>
            <div className="event-detail-backdrop" onClick={closeDetailPanel}></div>
            <div className="event-detail-panel">
              <button className="detail-close-button" onClick={closeDetailPanel}>×</button>

              {selectedEvent.image_url
                ? <img src={selectedEvent.image_url} alt={selectedEvent.name} className="detail-event-image" />
                : <div className="detail-image-placeholder">No Image</div>
              }

              <div className="event-detail-content">
                <h2 className="detail-title">{selectedEvent.name}</h2>

                {selectedEvent.event_owner && (
                  <div className="detail-line">
                    <span className="detail-label">Hosted by: </span>
                    {selectedEvent.event_owner}
                  </div>
                )}

                <div className="detail-line">
                  <span className="detail-label">Date: </span>
                  {formatDisplayDate(selectedEvent.date)}
                </div>

                {(selectedEvent.startTime || selectedEvent.endTime) && (
                  <div className="detail-line">
                    <span className="detail-label">Time: </span>
                    {selectedEvent.startTime || "TBA"}
                    {selectedEvent.endTime && ` to ${selectedEvent.endTime}`}
                  </div>
                )}

                {selectedEvent.admission && (
                  <div className="detail-line">
                    <span className="detail-label">Admission: </span>
                    {selectedEvent.admission}
                  </div>
                )}

                {selectedEvent.location && (
                  <div className="detail-line">
                    <span className="detail-label">Location: </span>
                    {selectedEvent.location}
                  </div>
                )}

                {selectedEvent.description && (
                  <p className="detail-description">{selectedEvent.description}</p>
                )}

                {selectedEvent.website && (
                  <p className="detail-website">
                    <span className="detail-label">Website: </span>
                    <a href={selectedEvent.website} target="_blank" rel="noreferrer">
                      {selectedEvent.website}
                    </a>
                  </p>
                )}
              </div>

              <div className="event-detail-footer">
                {isOwnEvent(selectedEvent) && !isPastEvent ? (
                  /* OWNER: Only sees Edit */
                  <button
                    className="edit-toggle"
                    onClick={() => {
                      closeDetailPanel();
                      onViewSelect("event-management", { editEvent: selectedEvent });
                    }}
                  >
                    ✎ Edit
                  </button>
                ) : (
                  /* PUBLIC: Only show 'Ended' notice if past, otherwise empty/hidden */
                  <>
                    {isPastEvent && (
                      <span className="go-question" style={{ opacity: 0.6, width: '100%', textAlign: 'center' }}>
                        This event has ended
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}

      </div>
      </div>
      </div>
    );
  }